import { useState, type FormEvent } from 'react';
import { Button, Group, Modal, Select, Stack, Text, TextInput } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '@/api/errors';
import { ROLES, useAddMember, type Role } from '@/api/orgs';
import { brand } from '@/theme/theme';

export interface AddMemberModalProps {
  orgId: string;
  opened: boolean;
  onClose: () => void;
}

/** Modal to invite a new organization member: email, name, role (with an uploader-role hint). */
export function AddMemberModal({ orgId, opened, onClose }: AddMemberModalProps) {
  const { t } = useTranslation();
  const addMember = useAddMember(orgId);
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<Role>('viewer');
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setEmail('');
    setFullName('');
    setRole('viewer');
    setError(null);
  };
  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    try {
      await addMember.mutateAsync({ email, full_name: fullName, role });
      handleClose();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <Modal opened={opened} onClose={handleClose} title={t('org.addMember')}>
      <form onSubmit={(e) => void handleSubmit(e)}>
        <Stack gap="md">
          <TextInput
            label={t('auth.email')}
            type="email"
            required
            withAsterisk={false}
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
          />
          <TextInput label={t('org.fullName')} value={fullName} onChange={(e) => setFullName(e.currentTarget.value)} />
          <Select
            label={t('org.role')}
            data={ROLES.map((r) => ({ value: r, label: t(`org.roles.${r}`) }))}
            value={role}
            allowDeselect={false}
            onChange={(value) => value && setRole(value as Role)}
          />
          {role === 'uploader' && (
            <Text fz={12} c={brand.textMuted}>
              {t('org.uploaderHint')}
            </Text>
          )}
          {error && (
            <Text c="red" role="alert">
              {error}
            </Text>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={handleClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={addMember.isPending}>
              {t('org.addMember')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
